import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { Icon } from 'leaflet';
import { fetchEvents } from '../api';
import OrganizationFilter from './OrganizationFilter';
import { getEventHosts, isEventCanceled } from '../utils/eventUtils';
import RegistrationLinks from './RegistrationLinks';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in Leaflet with React
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

import featuredIcon from 'leaflet/dist/images/marker-icon-2x.png';

let DefaultIcon = new Icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});

let FeaturedIcon = new Icon({
    iconUrl: featuredIcon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});

const defaultCenter = {
    lat: 33.7490, // Atlanta's latitude
    lng: -84.3880 // Atlanta's longitude
};

const EventMapView = ({ theme = {}, organizations = [], selectedOrg = 'all', onOrgChange, events = [] }) => {
    const [viewMode, setViewMode] = useState('upcoming');
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [eventsAtLocation, setEventsAtLocation] = useState([]);
    const [currentEventIndex, setCurrentEventIndex] = useState(0);

    // Default theme values if not provided
    const defaultTheme = {
        background: 'bg-white',
        text: 'text-gray-900',
        secondaryText: 'text-gray-600',
        link: 'text-blue-600 hover:text-blue-800',
        border: 'border-gray-200'
    };

    // Merge provided theme with defaults
    const currentTheme = { ...defaultTheme, ...theme };

    const handleMarkerClick = (event, clickedEvent) => {
        // Find all events at the same location
        const sameLocationEvents = events.filter(e => 
            e.lat === clickedEvent.lat && 
            e.lon === clickedEvent.lon
        );
        
        setEventsAtLocation(sameLocationEvents);
        setSelectedEvent(clickedEvent);
        setCurrentEventIndex(sameLocationEvents.findIndex(e => e.id === clickedEvent.id));
    };

    const handleNextEvent = () => {
        const nextIndex = (currentEventIndex + 1) % eventsAtLocation.length;
        setCurrentEventIndex(nextIndex);
        setSelectedEvent(eventsAtLocation[nextIndex]);
    };

    const handlePrevEvent = () => {
        const prevIndex = (currentEventIndex - 1 + eventsAtLocation.length) % eventsAtLocation.length;
        setCurrentEventIndex(prevIndex);
        setSelectedEvent(eventsAtLocation[prevIndex]);
    };

    return (
        <div className="flex flex-col h-full">
            <div className="flex flex-col space-y-4 mb-0 hidden">
                {/* Date Range Filter Buttons */}
                <div className="flex justify-center space-x-4">
                    {/* <button
                        onClick={() => setViewMode('upcoming')}
                        className={`py-2 px-4 rounded-lg transition flex items-center ${
                            viewMode === 'upcoming'
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                        }`}
                        aria-pressed={viewMode === 'upcoming'}
                    >
                        <i className="fas fa-calendar-day mr-2" aria-hidden="true"></i>
                        <span>Upcoming Events</span>
                    </button>

                    <button
                        onClick={() => setViewMode('month')}
                        className={`py-2 px-4 rounded-lg transition flex items-center ${
                            viewMode === 'month'
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                        }`}
                        aria-pressed={viewMode === 'month'}
                    >
                        <i className="fas fa-calendar-alt mr-2" aria-hidden="true"></i>
                        <span>This Month</span>
                    </button>

                    <button
                        onClick={() => setViewMode('next30')}
                        className={`py-2 px-4 rounded-lg transition flex items-center ${
                            viewMode === 'next30'
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                        }`}
                        aria-pressed={viewMode === 'next30'}
                    >
                        <i className="fas fa-calendar-week mr-2" aria-hidden="true"></i>
                        <span>Next 30 Days</span>
                    </button> */}
                </div>
            </div>

            <div className={`rounded-lg shadow-lg overflow-hidden z-0 ${currentTheme.background} flex-grow min-h-0`}>
                <MapContainer
                    center={[defaultCenter.lat, defaultCenter.lng]}
                    zoom={11}
                    style={{ height: '100%', width: '100%' }}
                    className="rounded-lg"
                    attributionControl={false}
                >
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    {events.map((event) => {
                        // Only show events with valid coordinates                        
                        
                        if (event.lat && event.lon) {
                            return (
                                <Marker
                                    key={event.id}
                                    position={[event.lat, event.lon]}
                                    eventHandlers={{
                                        click: (e) => {
                                            e.originalEvent.stopPropagation();
                                            handleMarkerClick(e, event);
                                        },
                                    }}
                                    icon={event.eventType === 'Featured' ? FeaturedIcon : DefaultIcon}
                                    opacity={isEventCanceled(event) ? 0.45 : 1}
                                >
                                    <Popup>
                                        <div className="p-2 max-w-xs">
                                            {selectedEvent && isEventCanceled(selectedEvent) && (
                                                <p className="bg-gray-300 text-gray-800 text-xs px-2 py-1 font-bold rounded-full mb-2 inline-block">
                                                    <i className="fas fa-ban mr-1" aria-hidden="true"></i>Canceled
                                                </p>
                                            )}
                                            <h3 className={`font-bold text-lg mb-2 ${selectedEvent && isEventCanceled(selectedEvent) ? 'line-through text-gray-500' : ''}`}>{selectedEvent?.title}</h3>
                                            {(() => {
                                                const hosts = selectedEvent ? getEventHosts(selectedEvent) : [];
                                                const multiHost = hosts.length > 1;
                                                return (
                                                    <p className="text-sm mb-1">
                                                        <strong>{multiHost ? 'Organizations: ' : 'Organization: '}</strong>
                                                        {multiHost ? hosts.map(h => h.organization).filter(Boolean).join(', ') : selectedEvent?.organization}
                                                    </p>
                                                );
                                            })()}
                                            <p className="text-sm mb-1">
                                                <strong>Location:</strong> {selectedEvent?.location}
                                            </p>
                                            <p className="text-sm mb-1"> <strong>Date: </strong>
                                                {selectedEvent?.startDate && new Date(selectedEvent.startDate).toLocaleString(undefined, {
                                                    month: 'long',
                                                    day: 'numeric',
                                                    year: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })}
                                            </p>

                                            {selectedEvent && isEventCanceled(selectedEvent) ? (
                                                <span className="inline-block bg-gray-200 text-gray-500 py-2 px-4 rounded">
                                                    <i className="fas fa-ban mr-2" aria-hidden="true"></i>Event Canceled
                                                </span>
                                            ) : selectedEvent && new Date(selectedEvent.endDate || selectedEvent.startDate) >= new Date(new Date().setHours(0, 0, 0, 0)) && (() => {
                                                return (
                                                    <RegistrationLinks
                                                        event={selectedEvent}
                                                        buttonClassName={`${currentTheme.button} py-2 px-4 rounded transition-colors`}
                                                        singleLinkClassName="inline-block"
                                                        multipleLinkClassName="block"
                                                    />
                                                );
                                            })()}
                                            {eventsAtLocation.length > 1 && (
                                                <div className="flex justify-between items-center mt-4 pt-2 border-t border-gray-200">
                                                    <button
                                                        onClick={handlePrevEvent}
                                                        className="bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                                                        aria-label="Previous event at this location"
                                                    >
                                                        ←
                                                    </button>
                                                    <span className="text-sm text-gray-600">
                                                        {currentEventIndex + 1} of {eventsAtLocation.length}
                                                    </span>
                                                    <button
                                                        onClick={handleNextEvent}
                                                        className="bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                                                        aria-label="Next event at this location"
                                                    >
                                                        →
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </Popup>
                                </Marker>
                            );
                        }
                        return null;
                    })}
                </MapContainer>
            </div>
        </div>
    );
};

export default EventMapView;
